/**
 * The guest shell's one REST surface: `${GATEWAY_URL}/guest/v1/auth/*`. `credentials: 'include'`
 * carries the guest session cookie; never an `Authorization` header — the guest never holds a
 * Keycloak token (ADR-001 Amendment 1).
 */
import { GATEWAY_URL } from '@/lib/gateway_url';
import { classifyGuestResponse, type GuestAnswer } from './classify';
import type { GuestSessionState } from './guest-types';

const BASE = `${GATEWAY_URL}/guest/v1/auth`;

async function send(method: 'GET' | 'POST', path: string, body?: unknown): Promise<Response | null> {
  try {
    return await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    return null;
  }
}

export async function postChallenge(token: string): Promise<GuestAnswer<never>> {
  const res = await send('POST', '/challenge', { token });
  return classifyGuestResponse(res, 'challenge');
}

export async function postVerify(token: string, otp: string): Promise<GuestAnswer<GuestSessionState>> {
  const res = await send('POST', '/verify', { token, otp });
  return classifyGuestResponse<GuestSessionState>(res, 'session');
}

export async function getSession(): Promise<GuestAnswer<GuestSessionState>> {
  const res = await send('GET', '/session');
  return classifyGuestResponse<GuestSessionState>(res, 'session');
}

/** Requests a fresh step-up code be sent to the guest's email. Never carries a code itself. */
export async function postStepUp(): Promise<GuestAnswer<never>> {
  const res = await send('POST', '/step-up');
  return classifyGuestResponse(res, 'challenge');
}

/**
 * Awaited, not fire-and-forget (ENT-566 decision, orchestrator/docs/prd/ent-566-task-first-f1.md:661,
 * overriding T08's original AC — see T08's amendment note): the shell must not claim `signedOut`
 * while the server session may still be valid, so it needs to know whether `/logout` actually
 * succeeded. Any non-2xx (the documented retryable `503` included) or network failure is `false`.
 */
export async function postLogout(): Promise<boolean> {
  const res = await send('POST', '/logout');
  return res !== null && res.status >= 200 && res.status < 300;
}
